import { OfferType, RankType } from '@smg-real-estate/hg-rets-schema';

import { createRequest } from '@/api/fetch';
import { normalizeRegionalHighlightAssignableListingsResponse } from '@/helpers/normalizeRegionalHighlightAssignableListingsResponse';
import { normalizeRegionalHighlightListingStatuses } from '@/helpers/normalizeRegionalHighlightListingStatuses';
import { normalizeRegionalHighlightPurchaseOptionsResponse } from '@/helpers/normalizeRegionalHighlightPurchaseOptionsResponse';
import {
  normalizeRegionalHighlightPurchaseOrder,
  normalizeRegionalHighlightPurchaseOrdersResponse,
} from '@/helpers/normalizeRegionalHighlightPurchaseOrdersResponse';
import { normalizeRegionalHighlightSlotsResponse } from '@/helpers/normalizeRegionalHighlightSlotsResponse';
import type { BookToplistingRequest, BookToplistingResponse } from '@/types';
import type {
  RegionalHighlightAssignableListingsRequest,
  RegionalHighlightAssignableListingsResponse,
  RegionalHighlightAssignmentRequest,
  RegionalHighlightAssignmentResponse,
  RegionalHighlightCallbackRequest,
  RegionalHighlightCallbackResponse,
  RegionalHighlightDeactivationRequest,
  RegionalHighlightDraftAddress,
  RegionalHighlightListingStatusRequest,
  RegionalHighlightListingStatusesByListingId,
  RegionalHighlightPurchaseOptionsResponse,
  RegionalHighlightPurchaseOrder,
  RegionalHighlightPurchaseOrderRequest,
  RegionalHighlightPurchaseOrdersResponse,
  RegionalHighlightSlotsResponse,
} from '@/types/regionalHighlight';

export enum ContractModel {
  ABO = 'ABO',
  DAILY = 'DAILY',
  PPA_BUNDLE = 'PPA_BUNDLE',
  LIFETIME = 'LIFETIME',
}

export interface ToplistingPrices {
  priceToplistingRent: number;
  priceToplistingBuy: number;
  priceToplistingPremium: number;
}

export interface TopListingAbos {
  totalSlots: number;
  associatedListerIds: Array<string>;
  activeSlots: number;
}

interface TopListingAbosResponse {
  data: {
    summary: TopListingAbos;
  };
}

export interface ToplistingCreditUsage {
  creditUsageForThisBookingInCHFCents: number;
  amountNotCoveredByCreditInCHFCents: number;
  agencyId: string;
  initialCreditInCHFCents: number;
  availableCreditInCHFCents: number;
  usedCreditInCHFCents: number;
  periodStartDate: string;
  periodEndDate: string;
  maximumTopListingDays: number | null;
  maximumPremiumListingDays: number | null;
  nextCreditInCHFCents: number;
  totalCostForThisBookingInCHFCents: number;
  nextCreditAvailableDate: string | null;
}

export interface LifetimeToplistingCreditUsage {
  availableCreditInCHFCents?: number;
  availableCreditAfterBookingTLRentInCHFCents?: number;
  availableCreditAfterBookingTLBuyInCHFCents?: number;
}

const createToplistingRequest = (method: 'GET' | 'PUT' | 'POST' | 'DELETE') =>
  createRequest({
    method,
    credentials: 'omit',
  });

const getToplistingServiceBaseUrl = (): string =>
  import.meta.env.VUE_APP_TOPLISTING_SERVICE_API_BASE_URL;

const regionalHighlightCustomerPath = (customerId: string): string =>
  `${getToplistingServiceBaseUrl()}/customers/${encodeURIComponent(customerId)}`;

interface RegionalHighlightDataResponse<T> {
  data: T;
}

interface RegionalHighlightListingStatusesData {
  statuses: RegionalHighlightListingStatusesByListingId;
}

export async function fetchTopListingAbos(listerId: string): Promise<TopListingAbos> {
  const endpoint = `${import.meta.env.VUE_APP_TOPLISTING_SERVICE_API_BASE_URL}/listers/${listerId}/tlabos`;

  const response = await createToplistingRequest('GET')(endpoint);
  const responseBody: TopListingAbosResponse = await response.json();

  return responseBody.data.summary;
}

export const bookToplisting = async (
  propertyReferenceId: string,
  toplistingData: BookToplistingRequest,
): Promise<BookToplistingResponse> => {
  const endpoint = `${
    import.meta.env.VUE_APP_TOPLISTING_SERVICE_API_BASE_URL
  }/toplistings/${encodeURIComponent(propertyReferenceId)}`;

  const response = await createToplistingRequest('PUT')(endpoint, JSON.stringify(toplistingData));

  return response.json();
};

export const bookToplistingAbo = async (
  propertyReferenceId: string,
  listerId: string,
): Promise<BookToplistingResponse> => {
  const endpoint = `${
    import.meta.env.VUE_APP_TOPLISTING_SERVICE_API_BASE_URL
  }/listers/${listerId}/toplistings/${encodeURIComponent(propertyReferenceId)}`;

  const body = {
    contractModel: ContractModel.ABO,
    productType: RankType.Top,
  };

  const response = await createToplistingRequest('PUT')(endpoint, JSON.stringify(body));

  return response.json();
};

export const deleteToplisting = async (propertyReferenceId: string): Promise<void> => {
  const endpoint = `${
    import.meta.env.VUE_APP_TOPLISTING_SERVICE_API_BASE_URL
  }/toplistings/${encodeURIComponent(propertyReferenceId)}`;

  const response = await createToplistingRequest('DELETE')(endpoint);

  return response.json();
};

export const fetchToplistingCreditUsage = async (
  offerType: OfferType,
  rankType: RankType,
  beginRequest: string,
  endRequest: string | null,
): Promise<ToplistingCreditUsage> => {
  let endpoint = `${import.meta.env.VUE_APP_TRANSACTION_SERVICE_API_BASE_URL}/credit-usage?`;
  endpoint += `offerType=${offerType}`;
  endpoint += `&productType=${rankType}`;
  endpoint += `&toplistingFromDate=${beginRequest}`;
  if (endRequest) {
    endpoint += `&toplistingToDate=${endRequest}`;
  }

  // NOTE: Always TRUE, until credit_info is removed from the backend
  endpoint += `&useCreditPeriod=true`;

  const response = await createToplistingRequest('GET')(endpoint);
  const responseBody: ToplistingCreditUsage = await response.json();

  return responseBody;
};

export const fetchLifetimeToplistingCreditUsage =
  async (): Promise<LifetimeToplistingCreditUsage> => {
    const endpoint = `${import.meta.env.VUE_APP_TRANSACTION_SERVICE_API_BASE_URL}/credit-usage-lifetime-tl`;
    const response = await createToplistingRequest('GET')(endpoint);
    const responseBody: LifetimeToplistingCreditUsage = await response.json();

    return responseBody;
  };

export async function fetchRegionalHighlightSlots(
  customerId: string,
  listingId?: string,
): Promise<RegionalHighlightSlotsResponse> {
  let endpoint = `${regionalHighlightCustomerPath(customerId)}/regional-highlight-slots`;
  if (listingId) {
    endpoint += `?listingId=${encodeURIComponent(listingId)}`;
  }
  const response = await createToplistingRequest('GET')(endpoint);
  const responseBody: unknown = await response.json();
  const responseRecord =
    typeof responseBody === 'object' && responseBody !== null
      ? (responseBody as { data?: unknown })
      : undefined;

  return normalizeRegionalHighlightSlotsResponse(responseRecord?.data ?? responseBody);
}

const appendNonBlankAddressParam = (
  query: URLSearchParams,
  key: 'locality' | 'street',
  value: string | undefined,
): void => {
  const trimmed = value?.trim();
  if (trimmed) {
    query.set(key, trimmed);
  }
};

export async function fetchRegionalHighlightSlotsForAddress(
  customerId: string,
  address: RegionalHighlightDraftAddress,
): Promise<RegionalHighlightSlotsResponse> {
  const query = new URLSearchParams({ postalCode: address.postalCode.trim() });
  appendNonBlankAddressParam(query, 'locality', address.locality);
  appendNonBlankAddressParam(query, 'street', address.street);
  if (Number.isFinite(address.latitude) && Number.isFinite(address.longitude)) {
    query.set('latitude', String(address.latitude));
    query.set('longitude', String(address.longitude));
  }

  const endpoint = `${regionalHighlightCustomerPath(customerId)}/regional-highlight-slots?${query.toString()}`;
  const response = await createToplistingRequest('GET')(endpoint);
  const responseBody: unknown = await response.json();
  const responseRecord =
    typeof responseBody === 'object' && responseBody !== null
      ? (responseBody as { data?: unknown })
      : undefined;

  return normalizeRegionalHighlightSlotsResponse(responseRecord?.data ?? responseBody);
}

const regionalHighlightPendingAssignmentPath = (
  customerId: string,
  propertyReferenceId: string,
): string =>
  `${regionalHighlightCustomerPath(customerId)}/regional-highlight-pending-assignments/${encodeURIComponent(propertyReferenceId)}`;

export async function recordRegionalHighlightPendingAssignment(
  customerId: string,
  propertyReferenceId: string,
  slotId: string,
): Promise<void> {
  await createToplistingRequest('PUT')(
    regionalHighlightPendingAssignmentPath(customerId, propertyReferenceId),
    JSON.stringify({ slotId }),
  );
}

export async function forgetRegionalHighlightPendingAssignment(
  customerId: string,
  propertyReferenceId: string,
): Promise<void> {
  await createToplistingRequest('DELETE')(
    regionalHighlightPendingAssignmentPath(customerId, propertyReferenceId),
  );
}

export async function fetchRegionalHighlightListingStatuses(
  customerId: string,
  request: RegionalHighlightListingStatusRequest,
): Promise<RegionalHighlightListingStatusesByListingId> {
  const endpoint = `${regionalHighlightCustomerPath(customerId)}/regional-highlight-listing-statuses`;
  const response = await createToplistingRequest('POST')(endpoint, JSON.stringify(request));
  const responseBody = await response.json();

  return normalizeRegionalHighlightListingStatuses(responseBody);
}

export async function fetchRegionalHighlightAssignableListings(
  customerId: string,
  slotId: string,
  request: RegionalHighlightAssignableListingsRequest,
): Promise<RegionalHighlightAssignableListingsResponse> {
  const query = new URLSearchParams({
    source: request.source,
    mode: request.mode,
  });
  if (request.page !== undefined) {
    query.set('page', String(request.page));
  }
  if (request.pageSize !== undefined) {
    query.set('pageSize', String(request.pageSize));
  }
  const trimmedQuery = request.q?.trim();
  if (trimmedQuery) {
    query.set('q', trimmedQuery);
  }

  const endpoint = `${regionalHighlightCustomerPath(customerId)}/regional-highlight-slots/${encodeURIComponent(slotId)}/assignable-listings?${query.toString()}`;
  const response = await createToplistingRequest('GET')(endpoint);
  const responseBody: unknown = await response.json();
  const responseRecord =
    typeof responseBody === 'object' && responseBody !== null
      ? (responseBody as { data?: unknown })
      : undefined;

  return normalizeRegionalHighlightAssignableListingsResponse(responseRecord?.data ?? responseBody);
}

export async function assignRegionalHighlightSlot(
  customerId: string,
  slotId: string,
  request: RegionalHighlightAssignmentRequest,
): Promise<RegionalHighlightAssignmentResponse> {
  const endpoint = `${regionalHighlightCustomerPath(customerId)}/regional-highlight-slots/${encodeURIComponent(slotId)}/assignment`;
  const response = await createToplistingRequest('PUT')(endpoint, JSON.stringify(request));
  const responseBody: RegionalHighlightDataResponse<RegionalHighlightAssignmentResponse> =
    await response.json();

  return responseBody.data;
}

export async function deactivateRegionalHighlightSlot(
  customerId: string,
  slotId: string,
  request: RegionalHighlightDeactivationRequest = {},
): Promise<void> {
  const endpoint = `${regionalHighlightCustomerPath(customerId)}/regional-highlight-slots/${encodeURIComponent(slotId)}/assignment`;
  const body = Object.keys(request).length > 0 ? JSON.stringify(request) : undefined;

  await createToplistingRequest('DELETE')(endpoint, body);
}

export async function requestRegionalHighlightCallback(
  customerId: string,
  request: RegionalHighlightCallbackRequest,
): Promise<RegionalHighlightCallbackResponse> {
  const endpoint = `${regionalHighlightCustomerPath(customerId)}/regional-highlight-callback-requests`;
  const response = await createToplistingRequest('POST')(endpoint, JSON.stringify(request));
  const responseBody: RegionalHighlightDataResponse<RegionalHighlightCallbackResponse> =
    await response.json();

  return responseBody.data;
}

const unwrapRegionalHighlightData = (responseBody: unknown): unknown => {
  if (typeof responseBody === 'object' && responseBody !== null && 'data' in responseBody) {
    const data = (responseBody as { data?: unknown }).data;
    if (data !== undefined) {
      return data;
    }
  }

  return responseBody;
};

export async function fetchRegionalHighlightPurchaseOptions(
  customerId: string,
): Promise<RegionalHighlightPurchaseOptionsResponse> {
  const endpoint = `${regionalHighlightCustomerPath(customerId)}/regional-highlight-purchase-options`;
  const response = await createToplistingRequest('GET')(endpoint);

  return normalizeRegionalHighlightPurchaseOptionsResponse(
    unwrapRegionalHighlightData(await response.json()),
  );
}

export async function createRegionalHighlightPurchaseOrder(
  customerId: string,
  request: RegionalHighlightPurchaseOrderRequest,
): Promise<RegionalHighlightPurchaseOrder> {
  const endpoint = `${regionalHighlightCustomerPath(customerId)}/regional-highlight-purchase-orders`;
  const response = await createToplistingRequest('POST')(endpoint, JSON.stringify(request));
  const order = normalizeRegionalHighlightPurchaseOrder(
    unwrapRegionalHighlightData(await response.json()),
  );

  if (!order) {
    throw new Error('Regional Highlight purchase order response was empty');
  }

  return order;
}

export async function fetchRegionalHighlightPurchaseOrders(
  customerId: string,
): Promise<RegionalHighlightPurchaseOrdersResponse> {
  const endpoint = `${regionalHighlightCustomerPath(customerId)}/regional-highlight-purchase-orders`;
  const response = await createToplistingRequest('GET')(endpoint);

  return normalizeRegionalHighlightPurchaseOrdersResponse(
    unwrapRegionalHighlightData(await response.json()),
  );
}

export async function extendRegionalHighlightSlot(
  customerId: string,
  slotId: string,
  request: RegionalHighlightPurchaseOrderRequest,
): Promise<RegionalHighlightPurchaseOrder> {
  return createRegionalHighlightPurchaseOrder(customerId, {
    ...request,
    renewSlotId: slotId,
  });
}

export async function setRegionalHighlightAutoRenew(
  customerId: string,
  slotId: string,
  autoRenew: boolean,
): Promise<void> {
  const endpoint = `${regionalHighlightCustomerPath(customerId)}/regional-highlight-slots/${encodeURIComponent(slotId)}/auto-renew`;
  await createToplistingRequest('PUT')(endpoint, JSON.stringify({ autoRenew }));
}
